// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import { useProfileQuery } from "@/api/hooks/use-profile";
import { KeyChip } from "@/components/shared/key-chip";
import { UserLink } from "@/components/shared/user-link";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

export function ProfileInfoTab() {
  const { t } = useTranslation("back-office");
  const { data: profile, isLoading, isError } = useProfileQuery();

  if (isLoading) {
    return (
      <div className="max-w-2xl space-y-3 p-6">
        <Skeleton className="h-5 w-64" />
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-5 w-56" />
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {t("errors.loadFailed")}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <div className="grid grid-cols-[160px_1fr] gap-y-3 text-sm">
        <span className="text-muted-foreground">{t("users.detail.fullName")}</span>
        <span>{profile.fullName || "—"}</span>

        <span className="text-muted-foreground">{t("users.list.columns.email")}</span>
        <span>
          <UserLink publicId={profile.publicId} legibleName={profile.email} showIcon={false} />
        </span>

        <span className="text-muted-foreground">{t("users.list.columns.publicId")}</span>
        <span>
          <KeyChip label={profile.publicId} />
        </span>

        <Separator className="col-span-2" />

        <span className="text-muted-foreground">{t("users.detail.groupsTitle")}</span>
        <span>
          {profile.groups.length > 0 ? (
            <span className="flex flex-wrap gap-1.5">
              {profile.groups.map((group) => (
                <Badge key={group} variant="secondary">
                  {group}
                </Badge>
              ))}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </span>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-medium">{t("users.detail.permissionsTitle")}</h3>
        {profile.permissions.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {profile.permissions.map((permission) => (
              <Badge key={permission} variant="outline" className="font-mono font-normal">
                {permission}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">—</p>
        )}
      </div>
    </div>
  );
}
